import { stringifyLosslessJson } from '@anchorbase/pillar-sdk';
import { z } from 'zod';
import {
    COMPLIANCE_LOG_TOPIC,
    RequestLogEntrySchema,
    type LogData,
    type LogException,
    type LogService,
    type LogSeverity,
    type LogTopic,
    type RequestLogEntry,
} from '@anchorbase/pillar-sdk';
import * as LoggingTransport from '@pillar-portal/logging-transport';
import type { LogWriteOptions } from '@pillar-portal/logging-transport';

type ErrorMessageCandidate = {
    name?: string;
    message?: string;
    stack?: string;
};

type BrowserContext = {
    url?: string;
    path?: string;
    query?: string;
    hash?: string;
    title?: string;
    user_agent?: string;
    visibility_state?: string;
};

type LoggerContext = {
    component: string;
    topics?: LogTopic[];
    subsystem?: string;
    workflow_id?: string;
    website_id?: string;
    correlation_id?: string;
    customer_id?: string;
    data?: LogData;
    sendToPillar?: boolean;
};

const FrontendService: LogService = 'pillar-frontend';
const DefaultTopics: LogTopic[] = ['frontend'];
const BatchWindowMs: number = 1000;
const MaxBatchSize: number = 10;

const ErrorCandidateSchema: z.ZodType<ErrorMessageCandidate> = z
    .object({
        name: z.string().optional(),
        message: z.string().optional(),
        stack: z.string().optional(),
    })
    .passthrough();

const LocationSchema = z
    .object({
        origin: z.string(),
        href: z.string(),
        pathname: z.string(),
        search: z.string().optional(),
        hash: z.string().optional(),
    })
    .passthrough();

const DocumentSchema = z
    .object({
        title: z.string().optional(),
        visibilityState: z.string().optional(),
    })
    .passthrough();

const NavigatorSchema = z
    .object({
        userAgent: z.string().optional(),
    })
    .passthrough();

const NonEmptyStringSchema: z.ZodType<string> = z.string().min(1);

const BrowserGlobalsSchema = z
    .object({
        location: LocationSchema.optional(),
        document: DocumentSchema.optional(),
        navigator: NavigatorSchema.optional(),
    })
    .passthrough();

const RandomUuidSchema: z.ZodType<() => string> = z.function().output(z.string());
const CryptoSchema: z.ZodType<{ randomUUID: () => string }> = z
    .object({
        randomUUID: RandomUuidSchema,
    })
    .passthrough();

let traceContextHeader: string | null = null;
let transportInstance: LoggingTransport.PillarLogTransportCore | null = null;
let flushTimeoutId: ReturnType<typeof setTimeout> | null = null;
const pendingEntries: Map<string, RequestLogEntry> = new Map();
const MaxInternalLogBufferSize: number = 100;
const internalLogBuffer: InternalLogEntry[] = [];

type InternalLogEntry = {
    timestamp: string;
    severity: LogSeverity;
    message: string;
    error?: string;
};

function recordInternalLog(entry: InternalLogEntry): void {
    internalLogBuffer.push(entry);
    if (internalLogBuffer.length > MaxInternalLogBufferSize) {
        internalLogBuffer.shift();
    }
}

export function setTraceContextHeader(headerValue: string): void {
    traceContextHeader = headerValue;
}

export type ComponentLogger = {
    debug: (message: string, data?: LogData) => void;
    info: (message: string, data?: LogData) => void;
    warn: (message: string, data?: LogData) => void;
    error: (message: string, error?: unknown, data?: LogData) => void;
};

export function createComponentLogger(
    component: string,
    contextOverrides: Omit<LoggerContext, 'component'> = {}
): ComponentLogger {
    return {
        debug: (message, data) =>
            void runLogWithSeverity('DEBUG', message, {
                component,
                data,
                ...contextOverrides,
            }),
        info: (message, data) =>
            void runLogWithSeverity('INFO', message, {
                component,
                data,
                ...contextOverrides,
            }),
        warn: (message, data) =>
            void runLogWithSeverity('WARNING', message, {
                component,
                data,
                ...contextOverrides,
            }),
        error: (message, error, data) =>
            void runLogWithSeverity('ERROR', message, {
                component,
                data,
                ...contextOverrides,
                error,
            }),
    };
}

type LogWithSeverityContext = LoggerContext & { error?: unknown };

async function logWithSeverity(
    severity: LogSeverity,
    message: string,
    context: LogWithSeverityContext
): Promise<void> {
    const enrichedEntry = buildLogEntry(severity, message, context);
    logToConsole(enrichedEntry);
    const sendToPillar = context.sendToPillar ?? true;
    if (sendToPillar) {
        await enqueueLogEntry(enrichedEntry);
    }
}

function runLogWithSeverity(
    severity: LogSeverity,
    message: string,
    context: LogWithSeverityContext
): Promise<void> {
    return logWithSeverity(severity, message, context).catch((error) => {
        recordInternalLog({
            timestamp: new Date().toISOString(),
            severity: 'ERROR',
            message: 'Failed to emit frontend log',
            error: String(error),
        });
    });
}

function buildLogEntry(
    severity: LogSeverity,
    message: string,
    context: LogWithSeverityContext
): RequestLogEntry {
    const clientTimestamp: string = new Date().toISOString();
    const operationId: string = generateOperationId();
    const browserContext: BrowserContext = resolveBrowserContext();
    const uiContextData: LogData = buildUiContextData(browserContext, context.component);
    const mergedData: LogData = {
        ...(context.data ?? {}),
        ...uiContextData,
    };
    const topics: LogTopic[] = context.topics ?? DefaultTopics;
    const traceId: string | undefined = resolveTraceId(traceContextHeader);
    const exception: LogException | undefined = buildLogException(context.error);
    const stackTrace: string | undefined = exception?.stack;

    return RequestLogEntrySchema.parse({
        client_timestamp: clientTimestamp,
        severity,
        service: FrontendService,
        topics,
        subsystem: context.subsystem ?? context.component,
        workflow_id: context.workflow_id,
        website_id: context.website_id,
        operation_id: operationId,
        correlation_id: context.correlation_id,
        trace_id: traceId,
        customer_id: context.customer_id,
        message,
        data: mergedData,
        exception,
        stack_trace: stackTrace,
    });
}

function buildUiContextData(context: BrowserContext, component: string): LogData {
    const uiContext = {
        component,
        ...(context.url !== undefined ? { url: context.url } : {}),
        ...(context.path !== undefined ? { path: context.path } : {}),
        ...(context.query !== undefined ? { query: context.query } : {}),
        ...(context.hash !== undefined ? { hash: context.hash } : {}),
        ...(context.title !== undefined ? { title: context.title } : {}),
        ...(context.user_agent !== undefined ? { user_agent: context.user_agent } : {}),
        ...(context.visibility_state !== undefined
            ? { visibility_state: context.visibility_state }
            : {}),
    };

    return {
        ui_context: uiContext,
    };
}

function buildLogException(error: unknown): LogException | undefined {
    if (error === undefined) {
        return undefined;
    }
    const parseResult = ErrorCandidateSchema.safeParse(error);
    if (!parseResult.success) {
        return undefined;
    }
    if (parseResult.data.message === undefined) {
        return undefined;
    }
    return {
        name: parseResult.data.name ?? 'Error',
        message: parseResult.data.message,
        stack: parseResult.data.stack,
    };
}

function resolveBrowserContext(): BrowserContext {
    const parseResult = BrowserGlobalsSchema.safeParse(globalThis);
    if (!parseResult.success) {
        return {};
    }
    const location = parseResult.data.location;
    const document = parseResult.data.document;
    const navigator = parseResult.data.navigator;

    return {
        url: location?.href,
        path: location?.pathname,
        query: location?.search,
        hash: location?.hash,
        title: document?.title,
        user_agent: navigator?.userAgent,
        visibility_state: document?.visibilityState,
    };
}

function resolveTraceId(headerValue: string | null): string | undefined {
    if (headerValue === null) {
        return undefined;
    }
    const traceHeaderParse = z.string().min(1).safeParse(headerValue);
    if (!traceHeaderParse.success) {
        return undefined;
    }
    const tracePart: string = traceHeaderParse.data.split(';')[0] ?? '';
    const traceIdCandidate: string = tracePart.split('/')[0] ?? '';
    const traceIdParse = z
        .string()
        .regex(/^[0-9a-f]{32}$/i)
        .safeParse(traceIdCandidate);
    if (!traceIdParse.success) {
        return undefined;
    }
    return traceIdParse.data.toLowerCase();
}

function generateOperationId(): string {
    const cryptoParseResult = CryptoSchema.safeParse(globalThis.crypto);
    if (!cryptoParseResult.success) {
        throw new Error('crypto.randomUUID is required to generate log operation_id');
    }
    return cryptoParseResult.data.randomUUID();
}

type ConsoleMirroredRequestLogEntry = RequestLogEntry & {
    data?: LogData;
};

function logToConsole(entry: ConsoleMirroredRequestLogEntry): void {
    const subsystemParse = NonEmptyStringSchema.safeParse(entry.subsystem);
    const componentName: string = subsystemParse.success
        ? subsystemParse.data
        : 'frontend';
    const logMessage: string = `[${componentName}] ${entry.message}`;
    recordInternalLog({
        timestamp: new Date().toISOString(),
        severity: entry.severity,
        message: logMessage,
        error: entry.exception?.message,
    });

    const consoleMethod: typeof console.debug = (() => {
        switch (entry.severity) {
            case 'DEBUG':
                return console.debug.bind(console);
            case 'INFO':
                return console.info.bind(console);
            case 'WARNING':
                return console.warn.bind(console);
            case 'ERROR':
                return console.error.bind(console);
            default:
                return console.log.bind(console);
        }
    })();

    const consoleArgs: unknown[] = [logMessage];
    if (entry.data !== undefined) {
        consoleArgs.push(entry.data);
    }
    if (entry.exception !== undefined) {
        consoleArgs.push(entry.exception);
    }
    consoleMethod(...consoleArgs);
}

async function enqueueLogEntry(entry: RequestLogEntry): Promise<void> {
    if (entry.topics.includes(COMPLIANCE_LOG_TOPIC)) {
        const options: LogWriteOptions = { awaitWrite: true };
        await getTransport().send(entry, options);
        return;
    }
    const dedupeKey = buildDedupeKey(entry);
    if (!pendingEntries.has(dedupeKey)) {
        pendingEntries.set(dedupeKey, entry);
    }
    if (pendingEntries.size >= MaxBatchSize) {
        await flushBatch();
        return;
    }
    scheduleFlush();
}

function buildDedupeKey(entry: RequestLogEntry): string {
    return stringifyLosslessJson({
        severity: entry.severity,
        message: entry.message,
        topics: entry.topics,
        subsystem: entry.subsystem,
        trace_id: entry.trace_id,
        correlation_id: entry.correlation_id,
        customer_id: entry.customer_id,
        args: entry.args,
        exception: entry.exception,
        stack_trace: entry.stack_trace,
    });
}

function scheduleFlush(): void {
    if (flushTimeoutId !== null) {
        return;
    }
    flushTimeoutId = setTimeout(() => {
        void flushBatch().catch((error) => {
            recordInternalLog({
                timestamp: new Date().toISOString(),
                severity: 'ERROR',
                message: 'Failed to flush log batch',
                error: String(error),
            });
        });
    }, BatchWindowMs);
}

async function flushBatch(): Promise<void> {
    if (flushTimeoutId !== null) {
        clearTimeout(flushTimeoutId);
        flushTimeoutId = null;
    }
    if (pendingEntries.size === 0) {
        return;
    }
    const entries = Array.from(pendingEntries.values());
    pendingEntries.clear();
    const options: LogWriteOptions = { awaitWrite: false };
    await getTransport().sendBatch(entries, options);
}

function getTransport(): LoggingTransport.PillarLogTransportCore {
    if (transportInstance !== null) {
        return transportInstance;
    }
    const portalOrigin = resolvePortalOrigin();
    transportInstance = new LoggingTransport.PillarLogTransportCore({
        getPortalUrl: () => portalOrigin,
        getTraceContextHeader: () => traceContextHeader,
        getRequestHeaders: async () => ({}),
    });
    return transportInstance;
}

function resolvePortalOrigin(): string {
    const parseResult = BrowserGlobalsSchema.safeParse(globalThis);
    if (!parseResult.success || !parseResult.data.location) {
        throw new Error('Browser location is required to resolve portal URL');
    }
    return parseResult.data.location.origin;
}
